import { z } from "zod";

/**
 * The bulk actions the server quotes before the user confirms them (PREC-4263): which leads of a
 * selection the action would cover, which it skips, and what one covered unit costs. The quote is
 * derived from the same rules the action runs with, for the whole selection (a select-all
 * included), and never runs or charges anything.
 */
export const CREDIT_QUOTE_ACTIONS = ["EMAIL", "PHONE", "COMPANY_CONTACT", "RESCORE"] as const;

export type CreditQuoteAction = (typeof CREDIT_QUOTE_ACTIONS)[number];

/**
 * One part of a quoted selection:
 * - `LOOKUP`: leads the email or phone lookup searches;
 * - `SKIPPED`: leads it leaves alone (an earlier lookup found nothing, a bounced address, no person,
 *   or for the email lookup an address the person already holds, PREC-4265);
 * - `COMPANY_LOOKUP`: companies missing an email or a phone, looked up once each;
 * - `COMPANY_COMPLETE`: companies that already hold both, skipped;
 * - `NO_COMPANY_DOMAIN`: leads without a company or a company website, skipped;
 * - `NOT_SCORED`: leads the current lead scoring has not scored yet, scored for free;
 * - `SCORED`: leads it already scored, rescored for a price only when the user asks for it.
 */
export const CREDIT_QUOTE_SEGMENT_KINDS = [
	"LOOKUP",
	"SKIPPED",
	"COMPANY_LOOKUP",
	"COMPANY_COMPLETE",
	"NO_COMPANY_DOMAIN",
	"NOT_SCORED",
	"SCORED",
] as const;

export type CreditQuoteSegmentKind = (typeof CREDIT_QUOTE_SEGMENT_KINDS)[number];

export const creditQuoteSegmentSchema = z.object({
	kind: z.enum(CREDIT_QUOTE_SEGMENT_KINDS),
	count: z.coerce.number(),
	/** What one unit of the segment costs when it is charged, in credits; 0 for a skipped one. */
	unitCredits: z.coerce.number(),
	/**
	 * The charged lookups the segment holds, when a unit can hold more than one: the email lookup
	 * searches a lead's person, its job offer and its Maps business, and charges each address it
	 * finds, so its maximum is `lookups × unitCredits`, not `count × unitCredits` (PREC-4263 review
	 * M1). Absent means one lookup per unit. `count` still names the leads, for the button.
	 */
	lookups: z.coerce.number().optional(),
});

export type CreditQuoteSegment = z.infer<typeof creditQuoteSegmentSchema>;

export const creditQuoteResponseSchema = z.object({
	action: z.enum(CREDIT_QUOTE_ACTIONS),
	/** How many leads the selection resolved to on the server. */
	selected: z.coerce.number(),
	segments: z.array(creditQuoteSegmentSchema),
});

export type CreditQuoteResponse = z.infer<typeof creditQuoteResponseSchema>;
